import React, { useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Typography,
  Stack,
  Pagination,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  Chip,
  useTheme
} from '@mui/material'
import { styled } from '@mui/material/styles'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import WorkIcon from '@mui/icons-material/Work'
import SchoolIcon from '@mui/icons-material/School'
import PublicIcon from '@mui/icons-material/Public'
import HomeWorkIcon from '@mui/icons-material/HomeWork'
import JobModal from './JobModal'
import AIChatBot from './AIChatBot'
import AIJobCard from './AIJobCard'

const JOBS_PER_PAGE = 10

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    color: theme.palette.grey[400],
    '&.Mui-checked': {
      color: theme.palette.error.main,
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.error.main,
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: theme.palette.grey[300],
  },
}))

// Helper to format currency
const formatCurrency = (value) => {
  if (!value) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

// Component to render JobListingArguments as chips
const SearchCriteriaChips = ({ jobListingArguments }) => {
  const theme = useTheme()

  if (!jobListingArguments) return null

  const chips = []

  // Specialties
  if (jobListingArguments.specialties?.length > 0) {
    jobListingArguments.specialties.forEach((specialty, idx) => {
      chips.push(
        <Chip
          key={`specialty-${idx}`}
          icon={<LocalHospitalIcon />}
          label={specialty}
          size="small"
          sx={{ backgroundColor: theme.palette.primary.light }}
        />
      )
    })
  }

  // Subspecialties
  if (jobListingArguments.subspecialties?.length > 0) {
    jobListingArguments.subspecialties.forEach((subspecialty, idx) => {
      chips.push(
        <Chip
          key={`subspecialty-${idx}`}
          icon={<LocalHospitalIcon />}
          label={subspecialty}
          size="small"
          variant="outlined"
          sx={{ borderColor: theme.palette.primary.main }}
        />
      )
    })
  }

  // Locations
  if (jobListingArguments.locations?.length > 0) {
    jobListingArguments.locations.forEach((location, idx) => {
      chips.push(
        <Chip
          key={`location-${idx}`}
          icon={<LocationOnIcon />}
          label={location.location}
          size="small"
          sx={{ backgroundColor: theme.palette.info.light }}
        />
      )
    })
  }

  // Salary
  if (jobListingArguments.salaryMinimum) {
    chips.push(
      <Chip
        key="salary-min"
        icon={<AttachMoneyIcon />}
        label={`Min ${formatCurrency(jobListingArguments.salaryMinimum)}`}
        size="small"
        sx={{ backgroundColor: theme.palette.success.light }}
      />
    )
  }

  if (jobListingArguments.salaryDesired) {
    chips.push(
      <Chip
        key="salary-desired"
        icon={<AttachMoneyIcon />}
        label={`Target ${formatCurrency(jobListingArguments.salaryDesired)}`}
        size="small"
        sx={{ backgroundColor: theme.palette.success.light }}
      />
    )
  }

  // Practice Types
  if (jobListingArguments.practiceTypes?.length > 0) {
    jobListingArguments.practiceTypes.forEach((type, idx) => {
      chips.push(
        <Chip
          key={`practice-${idx}`}
          icon={<WorkIcon />}
          label={type}
          size="small"
          sx={{ backgroundColor: theme.palette.warning.light }}
        />
      )
    })
  }

  // Academic Types
  if (jobListingArguments.academicTypes?.length > 0) {
    jobListingArguments.academicTypes.forEach((type, idx) => {
      chips.push(
        <Chip
          key={`academic-${idx}`}
          icon={<SchoolIcon />}
          label={type}
          size="small"
          sx={{ backgroundColor: theme.palette.secondary.light }}
        />
      )
    })
  }

  // Area Types
  if (jobListingArguments.areaTypes?.length > 0) {
    jobListingArguments.areaTypes.forEach((type, idx) => {
      chips.push(
        <Chip
          key={`area-${idx}`}
          icon={<HomeWorkIcon />}
          label={type}
          size="small"
          variant="outlined"
        />
      )
    })
  }

  // Visas
  if (jobListingArguments.visas?.length > 0) {
    jobListingArguments.visas.forEach((visa, idx) => {
      chips.push(
        <Chip
          key={`visa-${idx}`}
          icon={<PublicIcon />}
          label={visa}
          size="small"
          variant="outlined"
        />
      )
    })
  }

  // Boolean flags
  if (jobListingArguments.pslfCompatible) {
    chips.push(
      <Chip
        key="pslf"
        label="PSLF Compatible"
        size="small"
        color="success"
      />
    )
  }

  if (jobListingArguments.loanForgiveness) {
    chips.push(
      <Chip
        key="loan-forgiveness"
        label="Loan Forgiveness"
        size="small"
        color="success"
      />
    )
  }

  if (chips.length === 0) return null

  return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.12)', backgroundColor: 'white' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Search Criteria:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {chips}
      </Box>
    </Box>
  )
}

const AIJobInterface = ({ onFavoriteClick, favoritesOnly, toggleFavoritesFilter, signedIn, employerUserId }) => {
  const theme = useTheme()
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedJob, setSelectedJob] = useState(null)

  // Get AI job search state from Redux
  const {
    rankedJobs,
    jobListingArguments,
    loading,
    isAiTyping
  } = useSelector(state => state.aiJobSearch)

  // Filter jobs based on favorites
  const filterJobsByFavorites = (jobs) => {
    if (!favoritesOnly) return jobs
    return jobs.filter(rankedJob => rankedJob.job?.candidateJob?.favorite)
  }

  // Get filtered jobs
  const filteredJobs = filterJobsByFavorites(rankedJobs || [])

  // Pagination calculations
  const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE)
  const startIndex = (currentPage - 1) * JOBS_PER_PAGE
  const currentJobs = filteredJobs.slice(startIndex, startIndex + JOBS_PER_PAGE)

  // Reset page when favorites filter changes
  React.useEffect(() => {
    setCurrentPage(1)
  }, [favoritesOnly])

  // Event handlers
  const handleJobClick = useCallback(job => {
    setSelectedJob(job)
  }, [])

  const handleCloseModal = useCallback(() => {
    setSelectedJob(null)
  }, [])

  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value)
  }, [])

  return (
    <>
      <Grid
        container
        className="ai-job-results-container"
        sx={{
          height: '100%',
          maxHeight: 'calc(100vh - 64px)',
          marginRight: 0,
          transition: 'margin-right 0.3s ease',
        }}
      >
        {/* Main Panel - AI Results */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 2,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              backgroundColor: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant='h6' sx={{ color: theme.palette.secondary.main, fontWeight: 'bold' }}>
                AI Job Search Results
              </Typography>
            </Box>
            {signedIn && !employerUserId && (
              <FormControlLabel
                control={
                  <StyledSwitch
                    checked={favoritesOnly}
                    onChange={toggleFavoritesFilter}
                    icon={<FavoriteBorderIcon />}
                    checkedIcon={<FavoriteIcon />}
                  />
                }
                label='Favorites Only'
                labelPlacement='start'
                sx={{ marginRight: 0 }}
              />
            )}
          </Box>

          {/* Search Criteria Chips */}
          <SearchCriteriaChips jobListingArguments={jobListingArguments} />

          {/* Job List Content */}
          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            {filteredJobs.length > 0 ? (
              <>
                {/* Results Header */}
                <Box sx={{ p: 2, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant='body1' fontWeight='medium' color='text.primary'>
                    Found {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} matching your criteria
                    {favoritesOnly && ` (favorites only)`}
                  </Typography>
                  {totalPages > 1 && (
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color='primary'
                      size='small'
                    />
                  )}
                </Box>

                {/* Job Cards */}
                <Stack
                  spacing={1}
                  sx={{
                    px: 2,
                    flex: 1,
                    pb: 2,
                    minHeight: 0,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'rgba(0,0,0,0.1)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.secondary.main,
                      borderRadius: '3px',
                    }
                  }}
                >
                  {currentJobs.map(rankedJob => (
                    <AIJobCard
                      key={rankedJob.job?.id || rankedJob.jobId}
                      rankedJob={rankedJob}
                      isActive={selectedJob?.id === rankedJob.job?.id}
                      onClick={() => handleJobClick(rankedJob.job)}
                      onFavoriteClick={onFavoriteClick}
                    />
                  ))}
                </Stack>

                {/* Bottom Pagination */}
                {totalPages > 1 && (
                  <Box
                    sx={{
                      p: 2,
                      borderTop: 1,
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color='primary'
                      showFirstButton
                      showLastButton
                      size='small'
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                  textAlign: 'center',
                }}
              >
                {isAiTyping || loading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3
                    }}>
                      <CircularProgress
                        size={60}
                        sx={{
                          color: theme.palette.secondary.main,
                          '& .MuiCircularProgress-circle': {
                            strokeLinecap: 'round',
                          }
                        }}
                      />
                      <AutoAwesomeIcon
                        sx={{
                          position: 'absolute',
                          fontSize: '2rem',
                          color: theme.palette.secondary.main,
                          animation: 'sparkle 1.5s ease-in-out infinite',
                          '@keyframes sparkle': {
                            '0%': { opacity: 0.7, transform: 'scale(1)' },
                            '50%': { opacity: 1, transform: 'scale(1.2)' },
                            '100%': { opacity: 0.7, transform: 'scale(1)' },
                          }
                        }}
                      />
                    </Box>
                    <Typography
                      variant='h6'
                      sx={{
                        color: theme.palette.secondary.main,
                        fontWeight: 'bold',
                        mb: 1,
                        animation: 'pulse 1.5s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%': { opacity: 0.7 },
                          '50%': { opacity: 1 },
                          '100%': { opacity: 0.7 },
                        }
                      }}
                    >
                      Searching for jobs...
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Our AI is analyzing opportunities for you...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <AutoAwesomeIcon sx={{ fontSize: '3rem', color: theme.palette.secondary.main, mb: 2, opacity: 0.7 }} />
                    <Typography variant='h6' color='text.secondary' gutterBottom>
                      Ready to find your perfect job!
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {rankedJobs?.length > 0 && favoritesOnly
                        ? 'No favorite jobs found.'
                        : 'Use the chat to search for jobs that match your criteria.'}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Job Modal */}
      <JobModal job={selectedJob} onClose={handleCloseModal} />

      {/* AI Chat Drawer */}
      <AIChatBot />
    </>
  )
}

export default AIJobInterface
